import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import FadeIn from '../../../../../molecules/Transitions/FadeIn'
import Skeleton from '../../../../../atoms/Skeleton'
import { countRecursive, useStatusCounts } from '../../../hooks'
import { useStatusReportContext } from '../../../statusReportContext'
import Icon from '../../../../../atoms/Icon'
import { ICON_NAMES } from '../../../../../../constants'
import StatusReportStatusIcon from '../../../StatusReportStatusIcon'

const DEFAULT_ALL_ITEMS_LABEL = 'All Items'

const useStyles = makeStyles((theme) => ({
  summary: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 'var(--dim-6)',
    alignItems: 'center',
    justifyContent: 'start',
    height: '50px',
    position: 'relative',
    '&::before': {
      content: '""',
      width: '100%',
      borderBottom: '2px solid var(--color-neutral-300)',
      position: 'absolute',
      left: '0',
      right: '0',
      bottom: '0'
    }
  },
  cardItem: {
    display: 'flex',
    gridGap: '0.5em',
    alignItems: 'center',
    borderBottom: '2px solid transparent',
    fontSize: 'var(--font-size-sm)',
    height: '100%',
    position: 'relative',
    zIndex: 1,
    '&.selected': {
      borderBottom: '2px solid var(--color-primary-500)'
    }
  },
  displayName: {
    fontWeight: 'var(--font-weight-light)'
  },
  count: {
    backgroundColor: 'var(--color-neutral-200)',
    borderRadius: 'var(--border-radius-md)',
    padding: 'var(--dim-1)',
    fontSize: '0.75em',
    minWidth: '2em',
    textAlign: 'center'
  }
}))

function StatusReportSummaryBoardLayout ({ report, statuses, hidden, loading, cardWidth }) {
  const { currentFilter, toggleFilter, config } = useStatusReportContext()
  const counter = useStatusCounts(report, config?.countItemsAt)
  const allItemCount = useMemo(() => {
    if (!report?.items?.length) return []
    const counts = countRecursive(report.items, config?.countItemsAt)
    return Object.values(counts).reduce((a, b) => a + b, 0)
  }, [report, config])

  const sorted = useMemo(() => {
    if (loading) return []
    return [...statuses ?? []].filter(x => {
      if (!hidden?.length) return true
      return !hidden.includes(x.codeName)
    }).sort((a, b) => b.statusId - a.statusId)
  }, [statuses, loading, hidden])
  const classes = useStyles()

  if (loading) {
    return (
      <FadeIn delay={5} duration={5} className={classes.summary}>
        <Skeleton width='100%' height='100%' />
      </FadeIn>
    )
  }

  return (
    <div className={classes.summary}>
      <div
        className={clsx(classes.cardItem, currentFilter === null ? 'selected' : '')}
        onClick={() => toggleFilter(null)}
      >
        <Icon name={ICON_NAMES.radioUnchecked} customSize='1.15em' color='#000' />
        <span className={classes.displayName}>
          {config?.allItemsLabel ?? DEFAULT_ALL_ITEMS_LABEL}
        </span>
        <div className={classes.count}>{allItemCount}</div>
      </div>

      {sorted.map((s, i) => (
        <div
          className={clsx(classes.cardItem, currentFilter === s.statusId ? 'selected' : '')}
          onClick={() => toggleFilter(s.statusId)}
          key={s.codeName}
        >
          <StatusReportStatusIcon statusId={s.statusId} status={s} size={16} useIcon hasBackground />
          <span className={classes.displayName}>{s.displayName}</span>
          <div className={classes.count}>{counter(s.statusId)}</div>
        </div>
      ))}
    </div>
  )
}

StatusReportSummaryBoardLayout.propTypes = {
  report: PropTypes.shape({
    items: PropTypes.array
  }),
  statuses: PropTypes.arrayOf(PropTypes.shape({
    statusId: PropTypes.number,
    codeName: PropTypes.string,
    displayName: PropTypes.string,
    color: PropTypes.string
  })),
  hidden: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  cardWidth: PropTypes.string
}

export default StatusReportSummaryBoardLayout
